import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import type { ProductGroup } from '../../../infrastructure/ProductGroupClient';
import { collectLeafSkus } from '../../../pages/catalog/product-groups/utils/treeQueries';
import { useProductGroup } from './useProductGroup';

const PAGE_LIMIT = 200;

export interface UseProductGroupListResult {
  groups: ProductGroup[];
  filteredGroups: ProductGroup[];
  loading: boolean;
  error: string | null;
  searchQuery: string;
  setSearchQuery: (next: string) => void;
  refetch: () => Promise<void>;
  deleteGroup: (group: ProductGroup) => Promise<void>;
}

export function useProductGroupList(): UseProductGroupListResult {
  const productGroupClient = useProductGroup();

  const [groups, setGroups] = useState<ProductGroup[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const tombstonedGroupIdsRef = useRef<Set<string>>(new Set());

  const refetch = useCallback(async () => {
    setLoading(true);
    setError(null);
    const accumulatedGroups: ProductGroup[] = [];
    try {
      let cursor: string | undefined = undefined;
      do {
        const fetchedPage = await productGroupClient.list(
          cursor === undefined ? { limit: PAGE_LIMIT } : { limit: PAGE_LIMIT, cursor },
        );
        const survivingPageGroups = fetchedPage.productGroups.filter(
          (fetchedGroup) => !tombstonedGroupIdsRef.current.has(fetchedGroup.groupId),
        );
        accumulatedGroups.push(...survivingPageGroups);
        cursor = fetchedPage.nextCursor;
        setGroups([...accumulatedGroups]);
      } while (cursor);
    } catch (fetchError) {
      setError(fetchError instanceof Error ? fetchError.message : 'Failed to load product groups');
      if (accumulatedGroups.length === 0) {
        setGroups([]);
      }
    } finally {
      setLoading(false);
    }
  }, [productGroupClient]);

  useEffect(() => {
    void refetch();
  }, [refetch]);

  const deleteGroup = useCallback(
    async (group: ProductGroup) => {
      const memberCount = collectLeafSkus(group.members).length;
      const confirmMessage = `Delete product group "${group.groupName}"?\n\nThe ${memberCount} member product${memberCount === 1 ? '' : 's'} will be unlinked from this group.`;
      if (!window.confirm(confirmMessage)) {
        return;
      }
      let originalIndex = -1;
      tombstonedGroupIdsRef.current.add(group.groupId);
      setGroups((current) => {
        originalIndex = current.findIndex((existing) => existing.groupId === group.groupId);
        return current.filter((existing) => existing.groupId !== group.groupId);
      });
      const deleteToastId = toast.loading(`Deleting "${group.groupName}"…`);
      try {
        await productGroupClient.delete(group.groupId);
        toast.success(`Deleted "${group.groupName}"`, { id: deleteToastId });
      } catch (deleteError) {
        tombstonedGroupIdsRef.current.delete(group.groupId);
        setGroups((current) => {
          if (originalIndex < 0 || originalIndex > current.length) {
            return [...current, group];
          }
          const restoredGroups = current.slice();
          restoredGroups.splice(originalIndex, 0, group);
          return restoredGroups;
        });
        toast.error(deleteError instanceof Error ? deleteError.message : 'Failed to delete product group', {
          id: deleteToastId,
        });
      }
    },
    [productGroupClient],
  );

  const filteredGroups = useMemo(() => {
    const trimmedQuery = searchQuery.trim().toLowerCase();
    if (!trimmedQuery) {
      return groups;
    }
    return groups.filter((group) => {
      if (group.groupName.toLowerCase().includes(trimmedQuery)) {
        return true;
      }
      const memberSkus = collectLeafSkus(group.members);
      return memberSkus.some((sku) => sku.toLowerCase().includes(trimmedQuery));
    });
  }, [groups, searchQuery]);

  return { groups, filteredGroups, loading, error, searchQuery, setSearchQuery, refetch, deleteGroup };
}
